import React from 'react';
import { Typography, Stack, Box } from '@mui/material';
import SEOHead from './SEO/SEOHead';
import { styled } from '@mui/material/styles';
import ResponsiveContainer from '../components/layout/ResponsiveContainer';
import { Link as RouterLink } from 'react-router-dom';
import SectionTitle from './shared/SectionTitle';

const PageWrapper = styled('div')(({ theme }) => ({
    width: '100%',
    paddingBottom: theme.spacing(6)
}));

const HeroSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #1a2e4a 0%, #2a4a6e 100%)',
    color: '#ffffff',
    padding: theme.spacing(8, 2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='%23ffffff' fill-opacity='0.02'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px',
    },
}));

const SailImage = styled('img')(({ theme }) => ({
    width: '300px',
    maxWidth: '100%',
    height: 'auto',
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
        width: '100%'
    }
}));

const SailPicture = styled('picture')({});

const FeatureList = styled('ul')(({ theme }) => ({
    listStyle: 'none',
    padding: 0,
    margin: 0,
    '& li': {
        padding: '4px 0',
        position: 'relative',
        paddingLeft: '20px',
        '&:before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: theme.palette.orange.main
        }
    }
}));

const StyledLink = styled(RouterLink)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline'
    }
}));

interface SailTypeProps {
    title: string;
    image: string;
    imageWebp: string;
    imageWidth: number;
    imageHeight: number;
    description: string;
    features?: React.ReactNode[];
}

const SailTypeSection: React.FC<SailTypeProps> = ({
    title,
    image,
    imageWebp,
    imageWidth,
    imageHeight,
    description,
    features
}) => (
    <Box>
        <Typography variant="h4" align="center" sx={{ mb: 2 }}>
            {title}
        </Typography>
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 3 },
            alignItems: 'flex-start'
        }}>
            <Box sx={{ flexShrink: 0 }}>
                <SailPicture>
                    <source srcSet={imageWebp} type="image/webp" />
                    <SailImage
                        src={image}
                        alt={title}
                        width={imageWidth}
                        height={imageHeight}
                        loading="lazy"
                        decoding="async"
                    />
                </SailPicture>
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {description}
                </Typography>
                {features && (
                    <FeatureList>
                        {features.map((feature, index) => (
                            <li key={index}>
                                {typeof feature === 'string' ? (
                                    feature
                                ) : (
                                    <div>{feature}</div>
                                )}
                            </li>
                        ))}
                    </FeatureList>
                )}
            </Box>
        </Box>
    </Box>
);

const DownwindSailsPage = () => {
    const cruisingSpinnakers = {
        title: 'Cruising Spinnakers',
        image: '/images/DownwindSails_1.jpg',
        imageWebp: '/images/DownwindSails_1.webp',
        imageWidth: 800,
        imageHeight: 1200,
        description: "The Point Sails Asymmetric Cruising Spinnaker makes downwind sailing fun. Combining the easy handling of a genoa and the power of a spinnaker. It's simplicity makes it easy to sail with a short-handed crew. The Cruising Spinnaker is 15% smaller than our Racing Asymmetric making it easy to sail from 75 to 165 degrees AWA in very light winds to a strong 20 knot day. The tri-radial construction aligns the sail loads with the strong warp yarns of the spinnaker cloth. This sail can be customized with your choice of sail colors."
    };

    const racingSpinnakers = {
        title: 'Racing Spinnakers',
        image: '/images/DownwindSails_2.jpg',
        imageWebp: '/images/DownwindSails_2.webp',
        imageWidth: 800,
        imageHeight: 1200,
        description: 'Our line of asymmetric and symmetric racing spinnakers include:',
        features: [
            <span>Looking for a Code 0? Check out our <StyledLink to="/code-zero-sails">dedicated Code Zero page</StyledLink></span>,
            <span><strong>A2</strong> - The code 2 is a medium air running sail, used for apparent wind angles over 90 degrees.</span>,
            <span><strong>A3</strong> - The code 3 is a medium air reaching sail, used for apparent wind angles near 90 degrees</span>,
            <span><strong>S2</strong> - All-purpose symmetric runner</span>,
            <span><strong>S3</strong> - All-purpose symmetric reacher</span>
        ]
    };

    return (
        <PageWrapper>
            <SEOHead page="downwindSails" />
            
            {/* Navy Hero Section */}
            <HeroSection>
                <Box sx={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
                    <Typography 
                        variant="h2" 
                        sx={{ 
                            fontWeight: 500,
                            mb: 2,
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            color: '#ffffff',
                        }}
                    >
                        Downwind Sails
                    </Typography>
                    <Typography 
                        sx={{ 
                            fontSize: '1.15rem',
                            fontWeight: 300,
                            opacity: 0.9,
                            maxWidth: '600px',
                            margin: '0 auto',
                            color: '#ffffff',
                        }}
                    >
                        Expertly Designed and Constructed from High Quality Spinnaker Nylon and Code Zero Laminates
                    </Typography>
                </Box>
            </HeroSection>

            <ResponsiveContainer maxWidth="lg" sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
                    <SailTypeSection {...cruisingSpinnakers} />
                    <SailTypeSection {...racingSpinnakers} />

                    <Box>
                        <SectionTitle sx={{ mb: 1 }}>
                            Available in:
                        </SectionTitle>
                        <FeatureList>
                            <li>Challenge Fibermax (.75oz, 1.5oz, 2.2oz)</li>
                        </FeatureList>
                    </Box>
                </Box>
            </ResponsiveContainer>
        </PageWrapper>
    );
};

export default DownwindSailsPage;
